/**
 * LOGOI LAB - Mathematical Utilities
 * Pure functions for numerical operations
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

/**
 * Clamp a value between min and max bounds
 */
export function clamp(v: number, lo: number = 0, hi: number = 1): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Linear interpolation between two values
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Calculate percentile from array
 */
export function percentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * p)] || 0;
}

/**
 * Calculate mean of array
 */
export function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

/**
 * Calculate standard deviation
 */
export function std(arr: number[]): number {
  const m = mean(arr);
  return Math.sqrt(arr.reduce((a, v) => a + (v - m) ** 2, 0) / arr.length);
}

/**
 * Generate Gaussian random number using Box-Muller transform
 */
export function gaussianRandom(): number {
  const u1 = Math.max(0.0001, Math.random());
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * Wilson Score Confidence Interval
 * Used for binomial proportion estimation in Monte Carlo
 */
export function wilsonCI(successes: number, n: number, z: number = 1.96): {
  p: number;
  lower: number;
  upper: number;
} {
  if (n === 0) return { p: 0, lower: 0, upper: 0 };
  
  const p = successes / n;
  const denom = 1 + z * z / n;
  const center = (p + z * z / (2 * n)) / denom;
  const margin = z * Math.sqrt((p * (1 - p) + z * z / (4 * n)) / n) / denom;
  
  return { 
    p: +p.toFixed(4), 
    lower: +Math.max(0, center - margin).toFixed(4), 
    upper: +Math.min(1, center + margin).toFixed(4) 
  };
}

/**
 * Harmonic mean - useful for coherence calculations
 * A single low value drags down the entire result
 */
export function harmonicMean(values: number[]): number {
  if (values.length === 0) return 0;
  const EPSILON = 0.001;
  const floored = values.map(v => Math.max(v, EPSILON));
  return floored.length / floored.reduce((sum, v) => sum + 1/v, 0);
}

/**
 * Calculate variance
 */
export function variance(arr: number[]): number {
  const m = mean(arr);
  return arr.reduce((sum, v) => sum + (v - m) ** 2, 0) / arr.length;
}
